import {API} from "../../common/constants";
import {roundNum} from "../../common/utils";
import {Button, Divider, notification, Progress, Row, Upload} from "antd";
import React, {useState} from "react";
import {UploadOutlined} from "@ant-design/icons";
import {PageContainer} from "@ant-design/pro-components";
import axios from "axios";
import {auth} from "../../config/firebase";

type Props = {};

// Convert metadata for a song/album release from one format to another

const MetadataConverter = (props: Props) => {

    // ---- XLSX converter state ----
    const [fileList, setFileList] = useState<any[]>([]);
    const [uploading, setUploading] = useState<boolean>(false);
    const [converting, setConverting] = useState<boolean>(false);
    const [uploadProgress, setUploadProgress] = useState<number>();

    // ---- XML converter state ----
    const [xmlFileList, setXmlFileList] = useState<any[]>([]);
    const [xmlUploading, setXmlUploading] = useState<boolean>(false);
    const [xmlConverting, setXmlConverting] = useState<boolean>(false);
    const [xmlUploadProgress, setXmlUploadProgress] = useState<number>();

    // ---- Spotify XML converter state ----
    const [spotifyXmlFileList, setSpotifyXmlFileList] = useState<any[]>([]);
    const [spotifyUploading, setSpotifyUploading] = useState<boolean>(false);
    const [spotifyConverting, setSpotifyConverting] = useState<boolean>(false);
    const [spotifyUploadProgress, setSpotifyUploadProgress] = useState<number>();

    // ---- XLSX handlers ----
    const handleUpload = ({fileList}: any) => {
        if (fileList.length > 1) {
            notification.error({message: 'You can only upload one file at a time.'})
            return
        }
        setFileList(fileList)
    };

    const handleSubmit = async (event: { preventDefault: () => void; }) => {
        setUploading(true)
        event.preventDefault();

        let formData = new FormData();
        for (const file of fileList)
            formData.append("files", file.originFileObj);

        const access_token = await auth.currentUser?.getIdToken();

        axios
            .post(API + '/api/tools/metadata_converter/upload', formData,
                {
                    headers: {
                        "Accept": "*/*",
                        'Content-Type': 'multipart/form-data',
                        'Authorization': `Bearer ${access_token}`
                    },
                    onUploadProgress: (progressEvent) => {
                        const {loaded, total} = progressEvent
                        const percent = roundNum((loaded / total) * 100, 1)
                        setUploadProgress(percent)
                    }
                })
            .then(() => {
                setUploading(false)
                setConverting(true)
                axios
                    .post(API + '/api/tools/metadata_converter/convert', {
                            in_file_name: fileList[0].name,
                        },
                        {
                            headers: {
                                "Accept": "*/*",
                                'Content-Type': 'application/x-www-form-urlencoded;charset=utf-8',
                                Authorization: `Bearer ${access_token}`
                            },
                            responseType: 'blob',
                        }
                    ).then(res => {
                        setConverting(false)
                        notification.success({message: 'Successfully converted'})
                        const url = window.URL.createObjectURL(new Blob([res.data]));
                        const link = document.createElement('a');
                        link.href = url;
                        link.setAttribute('download', fileList[0].name + '_converted.xlsx');
                        document.body.appendChild(link);
                        link.click();
                        link.remove();
                        window.URL.revokeObjectURL(url);
                    })
                    .catch(() => {
                        setConverting(false)
                        notification.error({message: 'Conversion failed.'})
                    })
            })
            .catch(() => {
                setUploading(false)
                setConverting(false)
            });
    };

    // ---- XML handlers ----
    const handleXmlUpload = ({fileList}: any) => {
        if (fileList.length > 1) {
            notification.error({message: 'You can only upload one file at a time.'})
            return
        }
        setXmlFileList(fileList)
    };

    const handleXmlSubmit = async (event: { preventDefault: () => void }) => {
        setXmlUploading(true)
        event.preventDefault()

        const formData = new FormData()
        for (const file of xmlFileList)
            formData.append("files", file.originFileObj)

        const access_token = await auth.currentUser?.getIdToken()

        axios
            .post(API + '/api/tools/xml_metadata_converter/upload', formData, {
                headers: {
                    "Accept": "*/*",
                    'Content-Type': 'multipart/form-data',
                    'Authorization': `Bearer ${access_token}`
                },
                onUploadProgress: (progressEvent) => {
                    const {loaded, total} = progressEvent
                    const percent = roundNum((loaded / total!) * 100, 1)
                    setXmlUploadProgress(percent)
                }
            })
            .then(() => {
                setXmlUploading(false)
                setXmlConverting(true)
                axios
                    .post(API + '/api/tools/xml_metadata_converter/convert',
                        {in_file_name: xmlFileList[0].name},
                        {
                            headers: {
                                "Accept": "*/*",
                                'Content-Type': 'application/json',
                                Authorization: `Bearer ${access_token}`
                            },
                            responseType: 'blob',
                        }
                    )
                    .then(res => {
                        setXmlConverting(false)
                        notification.success({message: 'Successfully converted XML'})
                        const url = window.URL.createObjectURL(new Blob([res.data]))
                        const link = document.createElement('a')
                        link.href = url
                        link.setAttribute('download', xmlFileList[0].name.replace('.xml', '') + '_converted.xlsx')
                        document.body.appendChild(link)
                        link.click()
                        link.remove()
                        window.URL.revokeObjectURL(url)
                    })
                    .catch(() => {
                        setXmlConverting(false)
                        notification.error({message: 'XML conversion failed.'})
                    })
            })
            .catch(() => {
                setXmlUploading(false)
                notification.error({message: 'XML upload failed.'})
            })
    }

    // ---- Spotify XML handlers ----
    const handleSpotifyXmlUpload = ({fileList}: any) => {
        setSpotifyXmlFileList(fileList)
    };

    const handleSpotifyXmlSubmit = async (event: { preventDefault: () => void }) => {
        setSpotifyUploading(true)
        event.preventDefault()

        const formData = new FormData()
        for (const file of spotifyXmlFileList)
            formData.append("files", file.originFileObj)

        const access_token = await auth.currentUser?.getIdToken()

        axios
            .post(API + '/api/tools/spotify_xml_metadata_converter/upload', formData, {
                headers: {
                    "Accept": "*/*",
                    'Content-Type': 'multipart/form-data',
                    'Authorization': `Bearer ${access_token}`
                },
                onUploadProgress: (progressEvent) => {
                    const {loaded, total} = progressEvent
                    const percent = roundNum((loaded / total!) * 100, 1)
                    setSpotifyUploadProgress(percent)
                }
            })
            .then((uploadRes) => {
                setSpotifyUploading(false)
                setSpotifyConverting(true)
                axios
                    .post(API + '/api/tools/spotify_xml_metadata_converter/convert',
                        {session_id: uploadRes.data.session_id},
                        {
                            headers: {
                                "Accept": "*/*",
                                'Content-Type': 'application/json',
                                Authorization: `Bearer ${access_token}`
                            },
                            responseType: 'blob',
                        }
                    )
                    .then(res => {
                        setSpotifyConverting(false)
                        notification.success({message: 'Successfully converted Spotify XML'})
                        const url = window.URL.createObjectURL(new Blob([res.data]))
                        const link = document.createElement('a')
                        link.href = url
                        link.setAttribute('download', 'spotify_metadata.xlsx')
                        document.body.appendChild(link)
                        link.click()
                        link.remove()
                        window.URL.revokeObjectURL(url)
                    })
                    .catch(() => {
                        setSpotifyConverting(false)
                        notification.error({message: 'Spotify XML conversion failed.'})
                    })
            })
            .catch(() => {
                setSpotifyUploading(false)
                notification.error({message: 'Spotify XML upload failed.'})
            })
    }

    return (
        <PageContainer title={'Metadata Converter'}>

            {/* ---- XLSX → XLSX section ---- */}
            <Row style={{width: '35%'}}>
                <Upload
                    fileList={fileList}
                    onChange={handleUpload}
                    beforeUpload={() => false}
                    multiple={false}
                    accept={".xlsx"}
                >
                    <Button icon={<UploadOutlined/>}>Choose XLSX file</Button>
                </Upload>
                {uploadProgress !== undefined && uploadProgress !== 0 && fileList.length > 0 &&
                    <Progress percent={uploadProgress}/>}
            </Row>

            <br/>
            <Row>
                <Button onClick={handleSubmit} disabled={fileList.length === 0 || uploading || converting}
                        loading={uploading || converting}
                        type={"primary"}>
                    Convert XLSX
                </Button>
            </Row>

            <Divider/>

            {/* ---- XML → XLSX section ---- */}
            <Row style={{width: '35%'}}>
                <Upload
                    fileList={xmlFileList}
                    onChange={handleXmlUpload}
                    beforeUpload={() => false}
                    multiple={false}
                    accept={".xml"}
                >
                    <Button icon={<UploadOutlined/>}>Choose XML file</Button>
                </Upload>
                {xmlUploadProgress !== undefined && xmlUploadProgress !== 0 && xmlFileList.length > 0 &&
                    <Progress percent={xmlUploadProgress}/>}
            </Row>

            <br/>
            <Row>
                <Button onClick={handleXmlSubmit}
                        disabled={xmlFileList.length === 0 || xmlUploading || xmlConverting}
                        loading={xmlUploading || xmlConverting}
                        type={"primary"}>
                    Convert XML to Excel
                </Button>
            </Row>

            <Divider/>

            {/* ---- Spotify XML → XLSX section ---- */}
            <Row style={{width: '35%'}}>
                <Upload
                    fileList={spotifyXmlFileList}
                    onChange={handleSpotifyXmlUpload}
                    beforeUpload={() => false}
                    multiple={true}
                    accept={".xml"}
                >
                    <Button icon={<UploadOutlined/>}>Choose XML files (Spotify)</Button>
                </Upload>
                {spotifyUploadProgress !== undefined && spotifyUploadProgress !== 0 && spotifyXmlFileList.length > 0 &&
                    <Progress percent={spotifyUploadProgress}/>}
            </Row>

            <br/>
            <Row>
                <Button onClick={handleSpotifyXmlSubmit}
                        disabled={spotifyXmlFileList.length === 0 || spotifyUploading || spotifyConverting}
                        loading={spotifyUploading || spotifyConverting}
                        type={"primary"}>
                    Convert XML (Spotify)
                </Button>
            </Row>

        </PageContainer>
    );
};
export default MetadataConverter;

